import { ACT_SLUG, DAT_SLUG, LSAT_SLUG, MCAT_SLUG, SAT_SLUG } from 'my-core/course-utils';
import { numberToCurrency } from 'my-utils';

import booksIcon from 'images/icons/books.svg';
import brainIcon from 'images/icons/brain.svg';
import rocketIcon from 'images/icons/rocketship.svg';
import walletIcon from 'images/icons/wallet.svg';

export function hasAnyRoles(user, roles) {
  if (!user?.roles) return false;
  return (Array.isArray(roles) ? roles : [roles]).some(r => user.roles.includes(r));
}

export function hasManagerRole(user) {
  return hasAnyRoles(user, ['blog_manager', 'jobs_manager', 'marketing_manager', 'mcat_sales_rep']);
}

export function getUserFirstName(user) {
  return user?.first_name || user?.name?.split(' ')[0];
}
export function getUserLastName(user) {
  // /\s+([^\s]+)\s*$/ -- everything after last space
  // /\s+(.+)$/        -- everything after first space
  return user?.last_name || /\s+(.+)$/.exec(user?.name)?.[1];
}
export function getUserFullName(user) {
  if (!user) return;
  return user.name || [user.first_name, user.last_name].filter(Boolean).join(' ');
}
export function getUserInitials(user) {
  return (getUserFullName(user) || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');
}
// basically the 'display_name' or 'first name + last initial'
export function getUserDisplayName(user) {
  if (!user) return false;
  if (user.display_name) return user.display_name;
  const firstName = getUserFirstName(user);
  if (!firstName) return false;
  const initial = getUserLastName(user)?.charAt(0) || '';
  return `${firstName} ${initial}`;
}

export function getUserGreeting(user) {
  const hour = new Date().getHours();
  const name = getUserFirstName(user);

  let greeting;
  if (hour >= 23 || hour <= 2) greeting = 'Late Night';
  else if (hour <= 11) greeting = 'Good Morning';
  else if (hour <= 16) greeting = 'Good Afternoon';
  else greeting = 'Good Evening';

  return `${greeting}${name ? `, ${name}` : ''} 😄`;
}

export function isUserAccountSetup(user, role = 'student') {
  switch (role) {
    case 'tutor':
      return !!user.tutored_courses?.length;
    case 'student':
      return !!(
        user.school_id &&
        user.year &&
        (['high_school', 'k_to_8'].includes(user.school_type) || (user.faculty && user.degree))
      );
  }
  return true;
}

export function generatePassword(length = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghkmnopqrstuvwxyz23456789@#$%&*'; // removed easily confused chars: I, i, j l, 0, 1, O,
  return Array.from({ length }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
}

export const STUDY_STATS_CONFIG = [
  { key: 'mcat_coaching_sessions', label: 'Med-School Coaching Sessions Attended', mcat: true },
  { key: 'mcat_cars', label: 'Weekly CARS Passages Completed', mcat: true },
  { key: 'mcat_events', label: 'MCAT Events Attended', mcat: true },
  { key: 'hours_studied', label: 'Hours Studied' },
  { key: 'booster_savings', label: '$ Saved on Boosters', formatter: x => numberToCurrency(x) },
  { key: 'courses_studied', label: 'Courses Studied' },
  { key: 'practice_questions_attempted', label: 'Questions Attempted' },
  { key: 'questions_asked', label: 'Q&A Questions' },
];

export const FACULTIES_CONFIG = {
  sciences: { label: 'Science', icon: brainIcon },
  commerce: { label: 'Business', icon: walletIcon },
  engineering: { label: 'Engineering', icon: rocketIcon },
  arts: { label: 'Arts', icon: booksIcon },
  other: { label: 'Other' },
};

export const ADMISSIONS_ROLES = [
  'admissions_counselor',
  'admissions_editor',
  'admissions_interview_coach',
  'admissions_admin',
];

export const ROLES_CONFIG = {
  admin: {
    key: 'admin',
    label: 'Admin',
    summary: 'Full platform access; manages users, content, billing, and all administrative settings.',
  },
  instructor: {
    key: 'instructor',
    label: 'Instructor',
    summary: 'University-course instructor. Access to instructor dashboard online-courses and Q&A.',
  },
  ss_instructor: {
    key: 'ss_instructor',
    label: 'Grade-Boosters Instructor',
    summary: 'Grade-Boosters instructor dashboard. View past & upcoming events, edit online-course booklets',
  },
  tutor: {
    key: 'tutor',
    label: 'Tutor',
    summary: 'Provides paid 1-on-1 tutoring sessions to students, bookable through the Study Room.',
  },
  study_buddy_admin: {
    key: 'study_buddy_admin',
    label: 'Study Buddy Admin',
    summary: 'Receives notifications for new Study Buddy questions for triage and response.',
  },
  content_creator: {
    key: 'content_creator',
    label: 'Content Creator',
    summary: 'Creates and edits platform learning activities and online-course content.',
  },
  job_applicant: {
    key: 'job_applicant',
    label: 'Job Applicant',
    summary: 'A user who has applied for a staff/instructor position and is progressing through hiring.',
  },
  class_rep: {
    key: 'class_rep',
    label: 'Class Rep',
    summary: 'Student liaison hired per course-semester to communicate with instructors and upload study materials.',
  },
  school_manager: {
    key: 'school_manager',
    label: 'School Manager',
    summary: 'Reserved role for school-level administrative oversight (not currently wired up to any features).',
  },
  marketing_rep: {
    key: 'marketing_rep',
    label: 'Marketing Rep',
    summary: 'Campus contractor for flyering/work-shifts with a leaderboard, dashboard, and contractor invoicing.',
  },
  forum_moderator: {
    key: 'forum_moderator',
    label: 'Forum Moderator',
    summary: 'Subject-matter expert who receives notifications for every new WizeAnswers (Q&A) post.',
  },
  partner_member: {
    key: 'partner_member',
    label: 'Partner Member',
    summary: 'Member of a partner organization, granting access to partner-only online-courses and study groups.',
  },
  content_qa_specialist: {
    key: 'content_qa_specialist',
    label: 'QA Specialist',
    summary: 'QA editor who can edit any quiz question, activity, or lesson across all online-courses.',
  },
  teacher: {
    key: 'teacher',
    label: 'Teacher',
    summary: 'K-12 teacher with a dedicated dashboard for managing classes, lesson plans, grades, and student rosters.',
  },
  brand_ambassador: {
    key: 'brand_ambassador',
    label: 'Brand Ambassador',
    summary: 'Campus ambassador who can access the Brand Ambassador Dashboard and submit promotional invoices.',
  },
  marketing_manager: {
    key: 'marketing_manager',
    label: 'Marketing Manager',
    summary: 'Manages schools, course sections, staff work-shifts, and school marketing data.',
  },
  jobs_manager: {
    key: 'jobs_manager',
    label: 'Jobs Manager',
    summary: 'Creates and manages job postings and reviews all job applications.',
  },
  blog_manager: {
    key: 'blog_manager',
    label: 'Blog Manager',
    summary: 'Creates, edits, and publishes blog posts via the Admin Blog Manager.',
  },
  mcat_rep: {
    key: 'mcat_rep',
    label: 'MCAT Rep',
    summary: 'Campus MCAT-program contractor who submits invoices and has a dedicated MCAT rep dashboard.',
  },
  mcat_instructor: {
    key: 'mcat_instructor',
    label: 'MCAT Instructor',
    summary: 'Teaches MCAT course schedules and has full access to MCAT content, study sheets, and student progress.',
  },
  mcat_sales_rep: {
    key: 'mcat_sales_rep',
    label: 'MCAT Sales Rep',
    summary: 'Sales role that manages MCAT subscriptions, discount codes, custom products, and student enrollment.',
  },
  mcat_coach: {
    key: 'mcat_coach',
    label: 'MCAT Coach',
    summary: 'Access to MCAT student private messages & student profiles. Notified of new messages from students.',
  },
  lsat_instructor: {
    key: 'lsat_instructor',
    label: 'LSAT Instructor',
    summary: 'Teaches LSAT test-prep classes on the platform.',
  },
  lsat_coach: {
    key: 'lsat_coach',
    label: 'LSAT Coach',
    summary: 'Access to LSAT student private messages & student profiles. Notified of new messages from students.',
  },
  dat_instructor: {
    key: 'dat_instructor',
    label: 'DAT Instructor',
    summary: 'Teaches DAT test-prep classes on the platform.',
  },
  dat_coach: {
    key: 'dat_coach',
    label: 'DAT Coach',
    summary: 'Access to DAT student private messages & student profiles. Notified of new messages from students.',
  },
  sat_instructor: {
    key: 'sat_instructor',
    label: 'SAT Instructor',
    summary: 'Teaches SAT test-prep classes on the platform.',
  },
  sat_coach: {
    key: 'sat_coach',
    label: 'SAT Coach',
    summary: 'Access to SAT student private messages & student profiles. Notified of new messages from students.',
  },
  act_instructor: {
    key: 'act_instructor',
    label: 'ACT Instructor',
    summary: 'Teaches ACT test-prep classes on the platform.',
  },
  act_coach: {
    key: 'act_coach',
    label: 'ACT Coach',
    summary: 'Access to ACT student private messages & student profiles. Notified of new messages from students.',
  },
  mcat_tutor: {
    key: 'mcat_tutor',
    label: 'MCAT Tutor',
    summary:
      'Private MCAT, CASPer and undergraduate tutor linked to the contractor portal. Gives no coaching or course message access.',
  },
  lsat_tutor: {
    key: 'lsat_tutor',
    label: 'LSAT Tutor',
    summary: 'Private LSAT tutor linked to the contractor portal. Gives no coaching or course message access.',
  },
  dat_tutor: {
    key: 'dat_tutor',
    label: 'DAT Tutor',
    summary: 'Private DAT tutor linked to the contractor portal. Gives no coaching or course message access.',
  },
  sat_tutor: {
    key: 'sat_tutor',
    label: 'SAT Tutor',
    summary: 'Private SAT tutor linked to the contractor portal. Gives no coaching or course message access.',
  },
  act_tutor: {
    key: 'act_tutor',
    label: 'ACT Tutor',
    summary: 'Private ACT tutor linked to the contractor portal. Gives no coaching or course message access.',
  },
  admissions_counselor: {
    key: 'admissions_counselor',
    label: 'Admissions Counselor',
    summary: 'Admissions team counselor. Which clients they work with comes from their portal assignments.',
  },
  admissions_editor: {
    key: 'admissions_editor',
    label: 'Admissions Editor',
    summary: 'Admissions team editor. Which clients they work with comes from their portal assignments.',
  },
  admissions_interview_coach: {
    key: 'admissions_interview_coach',
    label: 'Admissions Interview Coach',
    summary: 'Admissions team interview coach. Which clients they work with comes from their portal assignments.',
  },
  admissions_admin: {
    key: 'admissions_admin',
    label: 'Admissions Admin',
    summary: 'Admissions admin pages only. No site admin powers and no pay approval.',
  },
  sales_demo_account: {
    key: 'sales_demo_account',
    label: 'Sales Demo Account',
    summary: 'Persistent demo account exempt from session re-authentication, used for sales demonstrations.',
  },
};

export function instructorRoleForCourse(courseOrSlug) {
  switch (courseOrSlug?.slug || courseOrSlug) {
    case LSAT_SLUG:
      return 'lsat_instructor';
    case MCAT_SLUG:
      return 'mcat_instructor';
    case DAT_SLUG:
      return 'dat_instructor';
    case SAT_SLUG:
      return 'sat_instructor';
    case ACT_SLUG:
      return 'act_instructor';
    default:
      return null;
  }
}

export function coachRoleForCourse(courseOrSlug) {
  switch (courseOrSlug?.slug || courseOrSlug) {
    case LSAT_SLUG:
      return 'lsat_coach';
    case MCAT_SLUG:
      return 'mcat_coach';
    case DAT_SLUG:
      return 'dat_coach';
    case SAT_SLUG:
      return 'sat_coach';
    case ACT_SLUG:
      return 'act_coach';
    default:
      return null;
  }
}
